import React, { useCallback, useEffect, useState } from "react";
import { Row, Col, Card, Statistic, Spin, Alert, Empty, Tag, Button } from "antd";
import {
    ClockCircleOutlined,
    ThunderboltOutlined,
    FileTextOutlined,
    TeamOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from "recharts";
import { api } from "../api";

const StatCard = ({ title, value, icon, color, suffix }) => (
    <Card variant="borderless" className="admin-stat-card">
        <div className="admin-stat-icon" style={{ background: color }}>
            {icon}
        </div>
        <Statistic title={title} value={value} suffix={suffix} />
    </Card>
);

const GroupDashboard = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        api.myAnalytics()
            .then(setData)
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (loading && !data) return <Spin size="large" style={{ marginTop: 80 }} />;
    if (error) return <Alert type="error" showIcon message={error} />;
    if (!data || !data.group)
        return (
            <Empty
                style={{ marginTop: 80 }}
                description="You don't have a group assigned yet."
            />
        );

    const { group, cards, registrationTrend } = data;

    const cardDefs = [
        {
            title: "Group Volunteer Hours",
            value: Number(cards.groupHours || 0).toFixed(1),
            icon: <ClockCircleOutlined />,
            color: "#a855f7",
        },
        {
            title: "Active Group Volunteers",
            value: cards.activeVolunteers || 0,
            icon: <ThunderboltOutlined />,
            color: "#0ea5e9",
        },
        {
            title: "Total Group Posts",
            value: cards.totalPosts || 0,
            icon: <FileTextOutlined />,
            color: "#16a34a",
        },
        {
            title: "Group Members",
            value: cards.members || 0,
            icon: <TeamOutlined />,
            color: "#2563eb",
        },
    ];

    return (
        <div>
            <div className="admin-page-head">
                <h1 className="admin-page-title">
                    {group.name}{" "}
                    {group.city && <Tag style={{ verticalAlign: "middle" }}>{group.city}</Tag>}
                </h1>
                <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
                    Refresh
                </Button>
            </div>

            <Row gutter={[24, 24]}>
                {cardDefs.map((c) => (
                    <Col xs={24} sm={12} lg={6} key={c.title}>
                        <StatCard {...c} />
                    </Col>
                ))}
            </Row>

            <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                <Col xs={24}>
                    <Card title="Group Volunteer Registration Trend" variant="borderless">
                        {registrationTrend.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={registrationTrend} margin={{ left: -16 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                                    <XAxis dataKey="month" fontSize={12} />
                                    <YAxis allowDecimals={false} fontSize={12} />
                                    <Tooltip />
                                    <Line
                                        type="monotone"
                                        dataKey="count"
                                        name="New members"
                                        stroke="#2563eb"
                                        strokeWidth={2.5}
                                        dot={{ r: 3 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default GroupDashboard;
